import React, {useMemo} from "react";
import {Outlet} from "react-router-dom";

import Navbar from "../components/Navbar";
import {Footer} from "../components/Footer";
import BackToTop from "../components/BackToTop";
import {useAuth} from "../context/AuthContext";
import {PERMISSIONS, ROLES} from "../lib/roles";

import logo from "../assets/logo_alt.png";

const BUSINESS_ROLES = new Set<string>([ROLES.ADMIN, ROLES.BUSINESS]);
// Employees reach the hub only when a business also granted them one of these.
const HUB_PERMISSIONS = new Set<string>([PERMISSIONS.SALES]);

const legal = [
    {label: "Privacy Policy", to: "/legal/privacy-policy"},
    {label: "Terms and Conditions", to: "/legal/terms-and-conditions"},
];

const social = {
    instagram: "https://www.instagram.com/namibia_events",
    facebook: "https://www.facebook.com/people/Namibia-Events/61559194141965",
};

type AppLayoutProps = {
    /** Navbar floats over a hero and solidifies on scroll. Used by the home route. */
    transparentNavbar?: boolean;

    /** Render the dark, solid navbar variant for content pages. */
    invertNavbar?: boolean;
};

export default function AppLayout({
                                      transparentNavbar = false,
                                      invertNavbar = false,
                                  }: AppLayoutProps) {
    const {user} = useAuth();

    const isBusiness = useMemo(() => {
        const roles = user?.roles ?? [];
        if (roles.some(r => BUSINESS_ROLES.has(r))) return true;
        return roles.includes(ROLES.EMPLOYEE)
            && (user?.permissions ?? []).some(p => HUB_PERMISSIONS.has(p));
    }, [user?.roles, user?.permissions]);

    // Full flat list, used by the footer's Quick Links / Resources columns.
    const footerMain = useMemo(() => [
        {label: "Home", to: "/"},
        {label: "Events", to: "/explore"},
        {label: "Tournaments", to: "/tournaments"},
        {label: "Calendar", to: "/calendar"},
        {label: "Cashless", to: "/cashless"},
        {label: "Memberships", to: "/memberships"},
        {label: "Pricing", to: "/#pricing"},
        {label: "About Us", to: "/about-us"},
        {label: "Help Center", to: "/help"},
        isBusiness
            ? {label: "Business Hub", to: "/business"}
            : {label: "List your Event", to: "/become-a-partner"},
    ], [isBusiness]);

    // Navbar groups Tournaments under Events, and Cashless/Memberships under Solutions.
    const navMain = useMemo(() => [
        {label: "Home", to: "/"},
        {
            type: "dropdown" as const,
            label: "Events",
            items: [
                {label: "All Events", to: "/explore"},
                {label: "Tournaments", to: "/tournaments"},
            ],
        },
        {label: "Calendar", to: "/calendar", mobileHidden: true},
        {
            type: "dropdown" as const,
            label: "Solutions",
            items: [
                {label: "Cashless", to: "/cashless"},
                {label: "Memberships", to: "/memberships"},
            ],
        },
        {label: "Pricing", to: "/#pricing"},
        {label: "About Us", to: "/about-us"},
        {label: "Help Center", to: "/help"},
        isBusiness
            ? {label: "Business Hub", to: "/business"}
            : {label: "List your Event", to: "/become-a-partner"},
    ], [isBusiness]);

    const navLinks = {main: navMain, legal, social};
    const footerLinks = {main: footerMain, legal, social};

    return (
        <section className="bg-white min-h-screen">
            <Navbar
                logo={logo}
                invert={invertNavbar}
                scrollSolidify={transparentNavbar}
                links={navLinks}
            />

            <Outlet/>

            <Footer logo={logo} links={footerLinks}/>

            <BackToTop/>
        </section>
    );
}
